import { FieldsMap, FieldViewModel, FieldViewModelImpl, ICropping, IField, IOmniaSettings, map } from "../../omnia"
import { BoundaryDrawingLayer } from "./layer"

import IMapTopologyService = omnia.map.IMapTopologyService
import Layer = omnia.map.Layer
import { ol_Feature, ol_format, ol_geom } from "@omnia/exports/openlayers"

export abstract class BoundaryDrawingContext {

    protected abstract validateInternal(feature: ol_Feature): boolean
    protected abstract updateFeatureGeometry(feature: ol_Feature): boolean

    private _wktFormat: ol_format.WKT
    private _changed: boolean
    private _editCount: number

    private _initialGeometry: ol_geom.Geometry
    private _initialGeometryWkt: string

    protected _layer: BoundaryDrawingLayer
    protected _backgroundLayer: Layer

    protected _fieldFeature: ol_Feature
    protected _initialArea: number

    protected _newBoundary: boolean

    validationErrors: any[]

    target: IField | ICropping
    targetIsCropping: boolean

    feature: ol_Feature
    newFeature: boolean

    isActive: boolean

    protected _allowDeletion: boolean
    protected _deleted: boolean

    public readonly commands: {
        label: string
        execute(): void
    }[] = []

    constructor(
        public map: FieldsMap,
        protected topo: IMapTopologyService,
        public switchToMapView: () => void,
        protected sourceLayer: omnia.map.Layer,
        protected settings: IOmniaSettings,
        public readonly targetBoundaryArea: number,
        public readonly copyBoundaryArea: {
            copy: (area: number) => void
            readonly enabled: boolean
        },
        protected readonly measure = topo.measure) {
    }

    get isInvalid(): boolean {
        return this.validationErrors && (this.validationErrors.length > 0)
    }

    get initialArea(): number {
        return this._initialArea
    }

    get currentArea(): number {

        if (this.map && (this.map.getEditMode() || this.map.getDrawingMode())) {
            return this.map.getEditedFeatureArea()
        }

        if (this.feature) {
            return this.measure.getArea(this.feature)
        }

        return this.initialArea

    }

    get drawingLayer() {
        return this._layer
    }

    getEditCommandText(): string {
        const msgDraw = "Draw the boundary"
        const msgEdit = "Edit the boundary"
        return this.newBoundary ? msgDraw : msgEdit
    }


    public dispose() {

        this.cleanupMap()

    }

    protected setupMap() {

        const map = this.map

        this._layer = new BoundaryDrawingLayer(this.sourceLayer)

        if (this._backgroundLayer) {
            map.addLayer(this._backgroundLayer)
        }

        map.addLayer(this._layer)
        map.setSelectableLayers([])

        map.synchronizeSelectionFromMap = false

    }

    protected cleanupMap() {

        const map = this.map

        this._layer = null
        this._backgroundLayer = null

        map.resetDefaultLayers(true)

        map.fieldsLayer.setHiddenFeatures(null)
        map.croppingLayer.setHiddenFeatures(null)

        map.synchronizeSelectionFromMap = true
        this.map.enableSelection = true

        var feature = this.findFeature(this.targetIsCropping, this.target)

        if (feature) {
            this.map.selectFeatures([feature])
        }

    }

    protected setupTarget(cropping: boolean, target: IField | ICropping) {

        this.targetIsCropping = cropping
        this.target = target

        var feature = this.findFeature(this.targetIsCropping, this.target)

        if (feature) {
            this.map.viewExtent(feature.getGeometry())
        }

        this._newBoundary = !feature

        this.map.enableSelection = false

    }


    private setupDrawing() {

        const feature = this.findFeature(this.targetIsCropping, this.target)

        this.newFeature = !feature

        if (feature) {

            this.sourceLayer.setHiddenFeatures([feature])

            this.feature = this._layer.createTargetFeatureFromSource(feature)

            if (this.feature) {
                this.map.viewExtent(this.feature.getGeometry())
            }

        }

        this._editCount = 0
        this._fieldFeature = this.findFeature(false, this.target.FieldId)

    }


    startDrawing() {

        if (!this._layer) {
            this.setupMap()
            this.setupDrawing()
        }

        var vm = this

        if (!this.feature) {
            this.feature = this.createDefaultFeature()
        }

        if (!this.feature) {

            if (!vm.map.startDrawingNewFeature(this._layer)) {
                return false
            }

            this._initialArea = null

        }
        else {

            this._initialArea = this.map.getFeatureArea(this.feature)

            this.fixFeatureGeometry(this.feature)

            if (!vm.map.startEditFeature(this.feature, (this._editCount == 0))) {
                return false
            }

            if (!this.initialGeometry) {

                const geom = this.feature.getGeometry()

                if (geom instanceof ol_geom.Polygon) {
                    this.initialGeometry = geom.clone()
                }

            }

        }

        this._editCount++

        return true

    }

    stopDrawing(cancel: boolean) {

        if (cancel) {

            this.map.cancelEdit()

            if (this._editCount == 1) {

                this._editCount = 0

                this.cleanupMap()

                this.map.enableSelection = false

                return
            }

        }
        else {

            let feature = <ol_Feature>this.map.commitEdit()

            if (this.newFeature && omnia.isObject(feature)) {

                feature.set("data", new FieldViewModelImpl(this.target, 0, () => true, this.targetIsCropping ? <FieldViewModel>{} : null))
                this._layer.setupNewTargetFeature(feature, false)

                this.newFeature = true
                this.feature = feature

            }


            feature = this.feature

            if (feature) {

                this.updateFeatureGeometry(feature)

            }

            this.updateChangeStatus()

        }

        this._layer.refreshTargetFeature()

        this.validate()

        return true

    }


    get editCount(): number {
        return this._editCount
    }

    get hasChanged(): boolean {
        return this.feature && (this._changed || this.newFeature)
    }

    get initialGeometry(): any {
        return this._initialGeometry
    }

    set initialGeometry(value: any) {
        this._initialGeometry = value
        this._initialGeometryWkt = (value ? this.getWktFormat().writeGeometry(value) : null)
    }

    get newBoundary(): boolean {
        return this._newBoundary
    }

    updateChangeStatus() {

        if (!(this.feature && this._initialGeometry)) {
            this._changed = false
            return
        }

        var wkt = this.getWktFormat().writeGeometry(this.feature.getGeometry())

        this._changed = (wkt != this._initialGeometryWkt)

    }


    cancelChanges() {

        if (this.feature) {

            if (this.newFeature) {
                this.map.removeFeatures([this.feature])
            }
            else if (this._initialGeometry) {
                this.feature.setGeometry(this._initialGeometry)
            }

        }

    }

    private getWktFormat() {

        if (!this._wktFormat) {
            this._wktFormat = new ol_format.WKT()
        }

        return this._wktFormat

    }

    getBoundaryWkt(): string {

        if (!this.hasChanged) {
            return null
        }

        return this.getWktFormat().writeGeometry(this.feature.getGeometry())

    }

    getBoundaryArea(): number {

        if (!this.hasChanged) {
            return null
        }

        return this.measure.getArea(this.feature)
    }

    findFeature(cropping: boolean, target: any): any {

        if (!target) return

        var vm = this
        var targetId = omnia.isNumber(target) ? target : (cropping ? target.CroppingId : target.FieldId)

        if (targetId) {

            var features = vm.map.findFeatures(f => {
                var data = <FieldViewModel>f.get("data")
                return (data && data.is(cropping, targetId))
            })

            if (features && (features.length == 1)) {

                return features[0]

            }

        }

    }

    createDefaultFeature(): ol_Feature {
        return null
    }

    validate(): boolean {

        this.validationErrors = []

        var feature = this.feature
        if (!feature) return true

        this.updateFeatureGeometry(feature)

        if (!this.validateInternal(feature)) {
            return false
        }

        let geometry = <any>feature.getGeometry()

        if (geometry.getType && (geometry.getType() != "Polygon")) {
            const msg = "Invalid boundary: the boundary cannot be saved because it is not made of a single polygon"
            this.addValidationMessage(msg)
            return false
        }

        return true

    }

    protected addValidationMessage(msg: string) {

        this.validationErrors = (this.validationErrors || []).concat(msg)

    }

    getBoundaryData() {

        if (this._deleted) {
            return {
                DeleteBoundary: true
            }
        }

        if (this._editCount && this.hasChanged) {

            return {
                Boundary: this.getBoundaryWkt(),
                BoundaryArea: Math.round(this.getBoundaryArea() / 100) / 100
            }

        }

    }

    setBoundaryData(data: any) {

        if (this._editCount && this.hasChanged) {
            data.Boundary = this.getBoundaryWkt()
            data.BoundaryArea = Math.round(this.getBoundaryArea() / 100) / 100
            return true
        }

    }

    enableMultiSelection(multiple: boolean) {

        if (this._editCount) return

        this.map.enableSelection = multiple
        this.map.enableMultipleSelection = multiple

        const cropping = this.targetIsCropping
        this.map.fieldsLayer.selectable = !cropping
        this.map.croppingLayer.selectable = cropping

    }

    canEditBoundary(): boolean {
        return true
    }

    checkFieldBoundary(): boolean {

        var field = (this.targetIsCropping ? (<ICropping>this.target).Field : (<IField>this.target))

        if (field) {
            return !!field.Boundary
        }
    }

    fixFeatureGeometry(feature: ol_Feature) {

        const geom = feature.getGeometry()

        if (!(geom instanceof ol_geom.Polygon)) {

            const fixed = omnia.map.getLargestPolygonFromArray([geom])

            if (fixed) {
                feature.setGeometry(fixed)
            }

        }
    }

    autoCropOnCommit(value: boolean): boolean {
        return false
    }

    canAutoCrop(): boolean {
        return false
    }

    autoCrop(): boolean {
        return false
    }

    supportsAutoCrop(): boolean {
        return false
    }

    get deleted() {
        return this._deleted
    }

    canDelete() {
        if (!this._allowDeletion) return false
        if (this.newBoundary) return false
        if (this.deleted) return false
        return true
    }


    delete() {

        this._deleted = true

        this.stopDrawing(true)

        if (this.feature) {
            this.feature.setStyle([])
        }

        return true

    }

    undelete() {
        this._deleted = false
        if (this.feature) {
            this.feature.setStyle(null)
        }
    }

    setBackgroundLayer(layer: Layer) {

        const map = this.map

        if (map && this._backgroundLayer) {
            this.map.removeLayer(this._backgroundLayer)
            this._backgroundLayer = null
        }

        this._backgroundLayer = layer

        if (map && layer && this._layer) {
            this.map.removeLayer(this._layer)
            this.map.addLayer(layer)
            this.map.addLayer(this._layer)
        }

        map.setSnappingLayers(this.map.getLayers())

    }


}
